import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { positionAt, trailUntil } from '../core/track.js';

import { MAP_TILES, tileOptions } from './mapTiles.js';

function markerIcon(color, initial, dimmed) {
    return L.divIcon({
        className: 'av-marker',
        html: `<span style="background:${color};opacity:${dimmed ? 0.55 : 1}">${initial}</span>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
    });
}

/**
 * Carte multi-pistes : trajet complet en pâle, trajet parcouru jusqu'à `t` en
 * plein, et un marqueur par participant à sa position interpolée.
 */
export default function TrackMap({ bundle, t, onSelectTrack }) {
    const containerRef = useRef(null);
    const mapRef = useRef(null);
    const layersRef = useRef({ base: {}, tracks: new Map() });
    const [mode, setMode] = useState('plan');

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !bundle) return undefined;
        // Une vue initiale est obligatoire avant d'ajouter des formes (cercle,
        // tracés) : Leaflet en a besoin pour les projeter. fitBounds la remplace ensuite.
        const map = L.map(container, {
            center: [46.6, 2.4],
            zoom: 5,
            zoomControl: true,
            attributionControl: true,
            zoomAnimation: false,
            fadeAnimation: false,
            markerZoomAnimation: false,
        });
        mapRef.current = map;
        const labelsPane = map.createPane('av-labels');
        labelsPane.style.zIndex = 450;
        labelsPane.style.pointerEvents = 'none';

        const base = {
            plan: L.tileLayer(MAP_TILES.plan.url, tileOptions('plan')),
            satellite: L.layerGroup([
                L.tileLayer(MAP_TILES.satellite.url, tileOptions('satellite')),
                L.tileLayer(MAP_TILES.satelliteLabels.url, tileOptions('satelliteLabels', { pane: 'av-labels' })),
            ]),
        };
        base.plan.addTo(map);
        layersRef.current.base = base;

        const bounds = [];
        if (bundle.zone?.polygon) {
            L.polygon(bundle.zone.polygon, { color: '#10b981', fillOpacity: 0.15, weight: 2, dashArray: '6 6' })
                .bindTooltip('Zone convenue').addTo(map);
            bounds.push(...bundle.zone.polygon);
        } else if (bundle.zone?.center) {
            L.circle([bundle.zone.center.lat, bundle.zone.center.lng], {
                radius: bundle.zone.radiusMeters || 100, color: '#10b981', fillOpacity: 0.12, weight: 2, dashArray: '6 6',
            }).bindTooltip('Zone convenue').addTo(map);
            bounds.push([bundle.zone.center.lat, bundle.zone.center.lng]);
        }

        const tracks = new Map();
        bundle.tracks.forEach(track => {
            const full = track.positions.map(p => [p.lat, p.lng]);
            bounds.push(...full);
            const responder = track.kind === 'responder';
            const ghost = L.polyline(full, { color: track.color, weight: responder ? 2 : 3, opacity: 0.25, dashArray: responder ? '6 6' : undefined }).addTo(map);
            const done = L.polyline([], { color: track.color, weight: responder ? 3 : 5, opacity: 0.95, dashArray: responder ? '6 6' : undefined }).addTo(map);
            const marker = L.marker([0, 0], {
                icon: markerIcon(track.color, responder ? '✚' : (track.label || '?').charAt(0).toUpperCase(), false),
                opacity: 0,
                zIndexOffset: 500,
            }).addTo(map);
            marker.bindTooltip(track.label, { direction: 'top', offset: [0, -14] });
            if (!responder) marker.on('click', () => onSelectTrack && onSelectTrack(track.id));
            tracks.set(track.id, { ghost, done, marker, lastStatus: null });
        });
        layersRef.current.tracks = tracks;

        if (bounds.length >= 2) map.fitBounds(L.latLngBounds(bounds), { padding: [30, 30], maxZoom: 17 });
        else if (bounds.length === 1) map.setView(bounds[0], 16);

        // Le conteneur change de taille (grille, plein écran) : sans
        // invalidateSize, Leaflet garde des tuiles grises.
        const observer = new ResizeObserver(() => map.invalidateSize());
        observer.observe(container);
        return () => {
            observer.disconnect();
            map.remove();
            mapRef.current = null;
        };
    }, [bundle, onSelectTrack]);

    useEffect(() => {
        const map = mapRef.current;
        const base = layersRef.current.base;
        if (!map || !base.plan) return;
        const [show, hide] = mode === 'plan' ? [base.plan, base.satellite] : [base.satellite, base.plan];
        if (map.hasLayer(hide)) map.removeLayer(hide);
        if (!map.hasLayer(show)) show.addTo(map);
    }, [mode]);

    useEffect(() => {
        if (!bundle || !Number.isFinite(t)) return;
        bundle.tracks.forEach(track => {
            const layers = layersRef.current.tracks.get(track.id);
            if (!layers) return;
            layers.done.setLatLngs(trailUntil(track.positions, t));
            const current = positionAt(track.positions, t);
            if (!current) {
                layers.marker.setOpacity(0);
                return;
            }
            layers.marker.setLatLng([current.lat, current.lng]);
            layers.marker.setOpacity(1);
            if (layers.lastStatus !== current.status) {
                layers.marker.setIcon(markerIcon(track.color, track.kind === 'responder' ? '✚' : (track.label || '?').charAt(0).toUpperCase(), current.status === 'before'));
                layers.lastStatus = current.status;
            }
        });
    }, [bundle, t]);

    return (
        <div className="av-map">
            <div ref={containerRef} className="av-map-canvas" />
            <button
                type="button"
                className="av-map-toggle"
                onClick={() => setMode(mode === 'plan' ? 'satellite' : 'plan')}
                title="Basculer plan / satellite"
            >
                {mode === 'plan' ? 'Satellite' : 'Plan'}
            </button>
        </div>
    );
}
